import "./scss/index.scss";
import { ReactNode, useEffect } from "react";
import { Box, Grid2, Toolbar } from "@mui/material";
import TopNavigation from "./TopNavigation";
import SideNavigation from "./SideNavigation";
import { useIsDesktopLayout } from "../../../hooks/useIsDesktopLayout";
import useDrawerStore from "../../../setup/store/useDrawerStore";
import useInitLookups from "@components/custom-hooks/useInitLookups";
import { useOrgBranding } from "../../../hooks/useOrgBranding";

interface Props {
  children: ReactNode;
}

const drawerWidth = 250;

const MasterLayout = ({ children }: Props) => {
  const isMediumScreen = useIsDesktopLayout();

  const { state: open, toggle } = useDrawerStore();
  const { orgName, faviconUrl } = useOrgBranding();

  // Update the browser tab title whenever the org name resolves.
  useEffect(() => {
    document.title = orgName;
  }, [orgName]);

  // Swap the browser-tab favicon to the org's own when it has configured one
  // (custom_fields.branding.favicon_url). Orgs without one are untouched — the
  // static /favicon.ico from index.html stays — so this is inert in prod where
  // no org sets a favicon. Restores the previous href on unmount/logout so the
  // login screen isn't left wearing the last tenant's icon.
  useEffect(() => {
    if (!faviconUrl) return;
    const link = document.head.querySelector<HTMLLinkElement>("link[rel~='icon']");
    if (!link) return;
    const previous = link.getAttribute("href");
    link.setAttribute("href", faviconUrl);
    return () => {
      if (previous != null) link.setAttribute("href", previous);
    };
  }, [faviconUrl]);

  useInitLookups();

  return (
    <Grid2 container>
      <TopNavigation />
      <SideNavigation open={open} onChange={toggle} width={drawerWidth} />
      <Box
        component="main"
        sx={{ flexGrow: 1, width: "100%" }}
        pl={isMediumScreen ? `${drawerWidth}px` : ""}
      >
        <Toolbar />
        {children}
      </Box>
    </Grid2>
  );
};

export default MasterLayout;
